/**
 * Utilities for If/Else workflow conditions: recipes, operators, config normalization, summaries.
 */

import { FALLBACK_TAG_OPTIONS } from './conditionFieldCatalog';

export const CONDITION_STEP_TYPES = new Set(['condition', 'if_else', 'branch']);

export const SCENARIO_RECIPES = [
  {
    id: 'build_your_own',
    label: 'Build your own',
    description: 'Select conditions based on your own criteria',
    requires: null,
  },
  {
    id: 'availability',
    label: 'Availability',
    description: 'Prepare branches based on opening and closing hours',
    requires: null,
  },
  {
    id: 'has_tag',
    label: 'Has tag',
    description: 'Prepare branches based on contact tags',
    requires: null,
  },
  {
    id: 'last_appointment_at',
    label: 'Last appointment at',
    description: "Create branches based on contact's last confirmed appointment",
    requires: null,
  },
  {
    id: 'contact_active_in_workflow',
    label: 'Contact Active in another workflow',
    description: 'Add branches based on whether or not the contact is active/inactive in another workflow',
    requires: null,
  },
  {
    id: 'email_events',
    label: 'Email events',
    description: 'Branch on email engagement events',
    requires: 'email_events',
    disabledReason: 'Add Email and Wait actions (wait for - email event) before the If/Else action to use this recipe',
  },
  {
    id: 'trigger_link_clicked',
    label: 'Trigger link clicked',
    description: 'Branch when a trigger link is clicked',
    requires: 'trigger_link_clicked',
    disabledReason: 'Add a Wait action (wait for - trigger link clicked) before the If/Else action to use this recipe',
  },
  {
    id: 'contact_replied',
    label: 'Contact replied',
    description: 'Branch when contact replies',
    requires: 'contact_replied',
    disabledReason: 'Add Email/SMS and Wait action (wait for - contact replied) before the If/Else action to use this recipe',
  },
  {
    id: 'order_submitted',
    label: 'Order Submitted for a Product',
    description: 'Branch on product order submission',
    requires: 'order_submitted_trigger',
    disabledReason: 'Add Order Submitted trigger to use this recipe',
  },
];

const TEXT_OPERATORS = [
  { value: 'is', label: 'Is' },
  { value: 'is_not', label: 'Is not' },
  { value: 'contains', label: 'Contains' },
  { value: 'does_not_contain', label: 'Does not contain' },
  { value: 'starts_with', label: 'Starts with' },
  { value: 'ends_with', label: 'Ends with' },
  { value: 'is_empty', label: 'Is empty' },
  { value: 'is_not_empty', label: 'Is not empty' },
];

const TAG_OPERATORS = [
  { value: 'includes', label: 'Includes' },
  { value: 'does_not_include', label: 'Does not include' },
  { value: 'is_not_empty', label: 'Is not empty' },
  { value: 'is_empty', label: 'Is empty' },
];

const NUMBER_OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Does not equal' },
  { value: 'greater_than', label: 'Greater than' },
  { value: 'greater_than_or_equal', label: 'Greater than or equal' },
  { value: 'less_than', label: 'Less than' },
  { value: 'less_than_or_equal', label: 'Less than or equal' },
  { value: 'is_empty', label: 'Is empty' },
  { value: 'is_not_empty', label: 'Is not empty' },
];

const DATE_OPERATORS = [
  { value: 'is', label: 'Is' },
  { value: 'is_before', label: 'Is before' },
  { value: 'is_after', label: 'Is after' },
  { value: 'is_between', label: 'Is between' },
  { value: 'is_empty', label: 'Is empty' },
  { value: 'is_not_empty', label: 'Is not empty' },
];

export function isConditionStep(step) {
  return CONDITION_STEP_TYPES.has(step?.type);
}

/** True when the workflow's last step is an If/Else condition (branch graph owns endings). */
export function workflowEndsWithConditionBranch(steps) {
  if (!Array.isArray(steps) || steps.length === 0) return false;
  return isConditionStep(steps[steps.length - 1]);
}

/** Main-chain "End of Workflow" is suppressed when branches own per-lane END nodes. */
export function shouldRenderMainChainEndNode(steps) {
  return !workflowEndsWithConditionBranch(steps);
}

/** Skip the post-step main-chain connector when a terminal condition branch graph is shown. */
export function isTerminalConditionBranchStep(step, stepIndex, steps, hasBranchRenderer = false) {
  if (!hasBranchRenderer || !isConditionStep(step)) return false;
  return stepIndex === steps.length - 1;
}

export function createBranchId() {
  return `branch_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

export function createDefaultIfElseConfig() {
  return {
    name: 'Condition',
    recipe: 'build_your_own',
    branches: [
      {
        id: createBranchId(),
        name: 'Branch',
        collapsed: false,
        conditions: [{ field: '', fieldLabel: '', operator: '', value: '', valueLabel: '' }],
        logic: 'AND',
        steps: [],
      },
    ],
    noneBranch: {
      id: 'none',
      name: 'None',
      label: 'When none of the conditions are met',
      steps: [],
    },
    configured: false,
  };
}

export const BRANCH_CARD_MIN_WIDTH = 280;
export const BRANCH_GRID_GAP = 16;

export function getConditionBranchLaneCount(step) {
  if (!isConditionStep(step)) return 0;
  const config = normalizeConditionConfig(step.config);
  return (config.branches?.length || 0) + 1;
}

export function computeConditionBranchGraphWidth(laneCount) {
  const count = Math.max(laneCount, 2);
  return count * BRANCH_CARD_MIN_WIDTH + (count - 1) * BRANCH_GRID_GAP;
}

/** Widest nested condition graph width required inside a single branch lane. */
export function computeNestedConditionGraphWidth(conditionStep) {
  if (!conditionStep || !isConditionStep(conditionStep)) {
    return BRANCH_CARD_MIN_WIDTH;
  }
  return computeConditionBranchLayout(conditionStep).graphWidth;
}

/** Effective horizontal space a branch lane must reserve for its child steps. */
export function computeBranchLaneContentWidth(branch = {}) {
  let widest = BRANCH_CARD_MIN_WIDTH;
  for (const childStep of branch.steps || []) {
    if (isConditionStep(childStep)) {
      widest = Math.max(widest, computeNestedConditionGraphWidth(childStep));
    }
  }
  return widest;
}

/**
 * Recursive branch graph layout: lane widths, graph width, and fork centers.
 * Lane width grows when a branch contains a nested If/Else graph wider than 280px.
 */
export function computeConditionBranchLayout(conditionStep) {
  if (!conditionStep || !isConditionStep(conditionStep)) {
    return {
      laneWidths: [],
      graphWidth: 0,
      laneCenterXs: [],
      laneCount: 0,
      nestedGraphWidthsByBranch: {},
    };
  }

  const config = normalizeConditionConfig(conditionStep.config);
  const lanes = [
    ...(config.branches || []).map((branch) => ({ ...branch, isNone: false })),
    {
      ...(config.noneBranch || createDefaultIfElseConfig().noneBranch),
      isNone: true,
      steps: config.noneBranch?.steps || [],
    },
  ];

  const laneWidths = lanes.map((branch) => computeBranchLaneContentWidth(branch));
  const laneCount = lanes.length;
  const graphWidth = laneWidths.reduce(
    (sum, width, index) => sum + width + (index > 0 ? BRANCH_GRID_GAP : 0),
    0,
  );

  const laneCenterXs = [];
  let offset = 0;
  laneWidths.forEach((width, index) => {
    if (index > 0) offset += BRANCH_GRID_GAP;
    laneCenterXs.push(offset + width / 2);
    offset += width;
  });

  const nestedGraphWidthsByBranch = {};
  lanes.forEach((branch) => {
    const branchKey = branch.id || (branch.isNone ? 'none' : 'branch');
    let widestNested = 0;
    for (const childStep of branch.steps || []) {
      if (isConditionStep(childStep)) {
        widestNested = Math.max(widestNested, computeNestedConditionGraphWidth(childStep));
      }
    }
    if (widestNested > BRANCH_CARD_MIN_WIDTH) {
      nestedGraphWidthsByBranch[branchKey] = widestNested;
    }
  });

  return {
    laneWidths,
    graphWidth,
    laneCenterXs,
    laneCount,
    nestedGraphWidthsByBranch,
  };
}

/** True when a branch lane's last step is an If/Else that owns nested branch endings. */
export function branchEndsWithConditionStep(branchSteps = []) {
  const lastStep = branchSteps[branchSteps.length - 1];
  return Boolean(lastStep && isConditionStep(lastStep));
}

export function computeConditionBranchGraphWidthForStep(conditionStep) {
  return computeConditionBranchLayout(conditionStep).graphWidth;
}

export function findConditionBranchLaneIndex(config, branchId) {
  const branchIds = [
    ...(config.branches || []).map((branch) => branch.id),
    config.noneBranch?.id || 'none',
  ];
  return branchIds.findIndex((id) => (
    id === branchId || (branchId === 'none' && (id === 'none' || id === config.noneBranch?.id))
  ));
}

/**
 * Compute global X center for a branch lane target, accounting for effective lane widths
 * and nested condition graphs along parentPath.
 */
export function computeBranchLaneGlobalCenterX({
  steps,
  conditionStepId,
  branchId,
  parentPath = [],
  mainGlobalCenterX,
  getBranchSteps = null,
}) {
  if (mainGlobalCenterX == null || !Array.isArray(steps) || steps.length === 0) {
    return null;
  }

  const resolveBranchSteps = getBranchSteps ?? ((config, branchKey) => {
    const normalized = normalizeConditionConfig(config);
    if (branchKey === 'none' || branchKey === normalized.noneBranch?.id) {
      return normalized.noneBranch?.steps || [];
    }
    const branch = (normalized.branches || []).find((item) => item.id === branchKey);
    return branch?.steps || [];
  });

  const topConditionStep = steps.find((step) => step.id === (parentPath[0]?.conditionStepId ?? conditionStepId))
    ?? steps.find((step) => isConditionStep(step));
  if (!topConditionStep || !isConditionStep(topConditionStep)) {
    return null;
  }

  let currentCondition = topConditionStep;
  const topLayout = computeConditionBranchLayout(topConditionStep);
  let graphLeft = mainGlobalCenterX - topLayout.graphWidth / 2;

  if (parentPath.length > 0) {
    for (const segment of parentPath) {
      const layout = computeConditionBranchLayout(currentCondition);
      const config = normalizeConditionConfig(currentCondition.config);
      const laneIndex = findConditionBranchLaneIndex(config, segment.branchId);
      const laneCenter = graphLeft + (layout.laneCenterXs[laneIndex] ?? 0);
      const branchSteps = resolveBranchSteps(config, segment.branchId);
      const nestedCondition = branchSteps.find((item) => item.id === conditionStepId && isConditionStep(item));
      if (!nestedCondition) {
        return null;
      }
      const nestedLayout = computeConditionBranchLayout(nestedCondition);
      graphLeft = laneCenter - nestedLayout.graphWidth / 2;
      currentCondition = nestedCondition;
    }
  } else if (currentCondition.id !== conditionStepId) {
    currentCondition = steps.find((step) => step.id === conditionStepId) || currentCondition;
  }

  const finalLayout = computeConditionBranchLayout(currentCondition);
  const finalConfig = normalizeConditionConfig(currentCondition.config);
  const targetLaneIndex = findConditionBranchLaneIndex(finalConfig, branchId);
  if (targetLaneIndex < 0) {
    return null;
  }
  return graphLeft + (finalLayout.laneCenterXs[targetLaneIndex] ?? 0);
}

export function computeConditionBranchLaneCenterXs(laneCount, graphWidth = null, laneWidths = null) {
  if (Array.isArray(laneWidths) && laneWidths.length > 0) {
    const centers = [];
    let offset = 0;
    laneWidths.forEach((width, index) => {
      if (index > 0) offset += BRANCH_GRID_GAP;
      centers.push(offset + width / 2);
      offset += width;
    });
    return centers;
  }

  const count = Math.max(laneCount, 2);
  const width = graphWidth ?? computeConditionBranchGraphWidth(count);
  return Array.from({ length: count }, (_, index) => (
    index * (BRANCH_CARD_MIN_WIDTH + BRANCH_GRID_GAP) + BRANCH_CARD_MIN_WIDTH / 2
  )).map((centerX) => Math.min(Math.max(centerX, BRANCH_CARD_MIN_WIDTH / 2), width - BRANCH_CARD_MIN_WIDTH / 2));
}

/**
 * Normalize legacy flat conditions[] config into branches[] shape.
 */
export function normalizeConditionConfig(config = {}) {
  if (config.branches?.length) {
    const base = createDefaultIfElseConfig();
    const normalized = {
      ...base,
      ...config,
      noneBranch: {
        ...base.noneBranch,
        ...(config.noneBranch || {}),
        steps: Array.isArray(config.noneBranch?.steps) ? config.noneBranch.steps : [],
      },
      branches: (config.branches || []).map((branch) => ({
        ...branch,
        steps: Array.isArray(branch.steps) ? branch.steps : [],
      })),
    };
    return normalized;
  }

  const legacyConditions = config.conditions || [];
  const mapped = legacyConditions.map((c) => ({
    field: c.field || '',
    fieldLabel: c.fieldLabel || c.field || '',
    operator: mapLegacyOperator(c.operator, c.field),
    value: Array.isArray(c.value) ? c.value : (c.value ? [c.value] : []),
    valueLabel: c.valueLabel || (Array.isArray(c.value) ? c.value.join(', ') : c.value || ''),
  }));

  return {
    name: config.name || config.actionName || 'Condition',
    recipe: config.recipe || 'build_your_own',
    branches: mapped.length
      ? [{
        id: createBranchId(),
        name: 'Branch',
        collapsed: false,
        conditions: mapped,
        logic: (config.logic || 'and').toUpperCase(),
      }]
      : createDefaultIfElseConfig().branches,
    noneBranch: config.noneBranch || createDefaultIfElseConfig().noneBranch,
    configured: config.configured || false,
  };
}

function mapLegacyOperator(operator, field) {
  if (!operator) return field === 'contact.tags' ? 'includes' : 'is';
  const map = {
    equals: 'is',
    not_equals: 'is_not',
    contains: 'contains',
    not_contains: 'does_not_contain',
    has_tag: 'includes',
    does_not_have_tag: 'does_not_include',
  };
  return map[operator] || operator;
}

export function getOperatorsForFieldType(fieldType) {
  switch (fieldType) {
    case 'tags':
      return TAG_OPERATORS;
    case 'number':
      return NUMBER_OPERATORS;
    case 'date':
      return DATE_OPERATORS;
    default:
      return TEXT_OPERATORS;
  }
}

export function operatorNeedsValue(operator) {
  return !['is_empty', 'is_not_empty'].includes(operator);
}

export function analyzeWorkflowContext(steps = [], currentStepId, trigger = null) {
  const idx = steps.findIndex((s) => s.id === currentStepId);
  const prior = idx >= 0 ? steps.slice(0, idx) : [...steps];

  const hasEmail = prior.some((s) => ['send_email', 'email_action'].includes(s.type));
  const hasSms = prior.some((s) => s.type === 'send_sms');
  const hasWait = prior.some((s) => s.type === 'wait' || s.type === 'wait_until');
  const hasAiAgent = prior.some((s) => s.type === 'ai_agent' || s.config?.action_key === 'ai_agent');

  const waitConfigMatches = (step, hints) => {
    const blob = JSON.stringify(step.config || {}).toLowerCase();
    return hints.some((h) => blob.includes(h));
  };

  const hasEmailEventWait = prior.some((s) =>
    s.type === 'wait' && waitConfigMatches(s, ['email_event', 'email event', 'opened', 'clicked']),
  );
  const hasTriggerLinkWait = prior.some((s) =>
    s.type === 'wait' && waitConfigMatches(s, ['trigger_link', 'trigger link', 'link_clicked']),
  );
  const hasContactRepliedWait = prior.some((s) =>
    s.type === 'wait' && waitConfigMatches(s, ['contact_replied', 'contact replied', 'replied']),
  );

  const triggerBlob = JSON.stringify(trigger || {}).toLowerCase();
  const hasOrderSubmittedTrigger = triggerBlob.includes('order_submitted') || triggerBlob.includes('order submitted');
  const hasOpportunityTrigger = triggerBlob.includes('opportunity');
  const hasFindOpportunity = prior.some((s) =>
    (s.config?.action_key || '').includes('opportunity') || (s.label || '').toLowerCase().includes('opportunity'),
  );

  return {
    email_events: hasEmail && hasEmailEventWait,
    trigger_link_clicked: hasTriggerLinkWait,
    contact_replied: (hasEmail || hasSms) && hasContactRepliedWait,
    order_submitted_trigger: hasOrderSubmittedTrigger,
    opportunity_context: hasOpportunityTrigger || hasFindOpportunity,
    ai_agent_above: hasAiAgent,
    contact_replied_wait: (hasEmail || hasSms) && hasContactRepliedWait,
  };
}

export function getRecipeAvailability(recipe, context) {
  if (!recipe.requires) return { available: true };
  const available = Boolean(context[recipe.requires]);
  return {
    available,
    disabledReason: available ? null : recipe.disabledReason,
  };
}

export function isCategoryAvailable(category, context) {
  if (!category.requires) return true;
  return Boolean(context[category.requires]);
}

export function formatConditionLine(condition) {
  if (!condition?.fieldLabel && !condition?.field) return '';
  const field = condition.fieldLabel || condition.field;
  const op = (condition.operator || '').replace(/_/g, ' ');
  const values = Array.isArray(condition.value)
    ? condition.value
    : (condition.value ? [condition.value] : []);
  const valueLabel = condition.valueLabel || values.join(', ');
  if (!operatorNeedsValue(condition.operator)) {
    return `${field} ${op}`.trim();
  }
  return `${field} ${op} ${valueLabel}`.trim();
}

export function formatBranchSummary(branch) {
  const lines = (branch.conditions || [])
    .map(formatConditionLine)
    .filter(Boolean);
  if (!lines.length) return 'No conditions set';
  const joiner = (branch.logic || 'AND') === 'OR' ? ' OR ' : ' AND ';
  return lines.join(joiner);
}

export function resolveTagOptions(apiTags = []) {
  if (apiTags.length) {
    return apiTags.map((t) => (typeof t === 'string' ? t : t.name || t.id)).filter(Boolean);
  }
  return [...FALLBACK_TAG_OPTIONS];
}

export function applyRecipeToBranches(recipeId, branches, tagOptions = []) {
  if (recipeId === 'has_tag' && tagOptions.length) {
    return [{
      id: createBranchId(),
      name: 'Branch',
      collapsed: false,
      logic: 'AND',
      conditions: [{
        field: 'contact.tags',
        fieldLabel: 'Tags',
        operator: 'includes',
        value: [tagOptions[0]],
        valueLabel: tagOptions[0],
      }],
    }];
  }
  if (recipeId === 'last_appointment_at') {
    return [{
      id: createBranchId(),
      name: 'Branch',
      collapsed: false,
      logic: 'AND',
      conditions: [{
        field: 'contact.last_appointment_at',
        fieldLabel: 'Last appointment at',
        operator: 'is_not_empty',
        value: [],
        valueLabel: '',
      }],
    }];
  }
  return branches;
}
